import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  alpha,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import axiosInstance from '../../../lib/axios';
import { useSnackbar } from '../../../components/SnackbarProvider';

interface SaleItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface Sale {
  id: string;
  receiptNumber?: string;
  totalAmount: number;
  subtotal?: number;
  tax?: number;
  discount?: number;
  paymentMethod: string;
  isRefunded: boolean;
  refundedAmount: number;
  refundedAt: string | null;
  items: SaleItem[];
  createdAt: string;
  isOrder?: boolean; // Flag to indicate this is an Order (not a Sale)
}

interface RefundModalProps {
  open: boolean;
  sale: Sale;
  onClose: () => void;
  onSuccess: () => void;
}

interface SelectedItem {
  saleItemId: string;
  quantity: number;
  maxQuantity: number;
  unitPrice: number;
  productName: string;
}

const REFUND_REASONS = [
  'Customer Request',
  'Defective Product',
  'Wrong Item',
  'Damaged in Transit',
  'Price Adjustment',
  'Other',
];

export const RefundModal: React.FC<RefundModalProps> = ({
  open,
  sale,
  onClose,
  onSuccess,
}) => {
  const { showMessage } = useSnackbar();

  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map());
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ reason?: string; items?: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setRefundType('full');
      setSelectedItems(new Map());
      setReason('');
      setNotes('');
      setErrors({});
      setApiError(null);
      setIsLoading(false);
    }
  }, [open]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleItemToggle = (item: SaleItem) => {
    const newSelected = new Map(selectedItems);
    if (newSelected.has(item.id)) {
      newSelected.delete(item.id);
    } else {
      newSelected.set(item.id, {
        saleItemId: item.id,
        quantity: item.quantity,
        maxQuantity: item.quantity,
        unitPrice: item.unitPrice,
        productName: item.productName,
      });
    }
    setSelectedItems(newSelected);
    setErrors((prev) => ({ ...prev, items: undefined }));
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    const newSelected = new Map(selectedItems);
    const item = newSelected.get(itemId);
    if (item) {
      const validQuantity = Math.max(1, Math.min(quantity, item.maxQuantity));
      newSelected.set(itemId, { ...item, quantity: validQuantity });
      setSelectedItems(newSelected);
    }
  };

  const calculateRefundAmount = () => {
    if (refundType === 'full') {
      return sale.totalAmount - sale.refundedAmount;
    }
    let total = 0;
    selectedItems.forEach((item) => {
      total += item.quantity * item.unitPrice;
    });
    return total;
  };

  const validateForm = (): boolean => {
    const newErrors: { reason?: string; items?: string } = {};

    if (!reason) {
      newErrors.reason = 'Reason is required';
    }

    if (refundType === 'partial' && selectedItems.size === 0) {
      newErrors.items = 'Select at least one item to refund';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      // Build items array based on whether this is an order or sale
      const items = refundType === 'partial'
        ? Array.from(selectedItems.values()).map((item) =>
            sale.isOrder
              ? { orderItemId: item.saleItemId, quantity: item.quantity }
              : { saleItemId: item.saleItemId, quantity: item.quantity }
          )
        : undefined;

      const payload = {
        reason,
        notes: notes.trim() || undefined,
        items,
      };

      // Use correct endpoint based on whether this is an Order or a Sale
      const endpoint = sale.isOrder
        ? `/orders/${sale.id}/refund`
        : `/sales/${sale.id}/refund`;
      await axiosInstance.post(endpoint, payload);

      showMessage('Refund processed successfully', 'success');
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to process refund';
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const refundAmount = calculateRefundAmount();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: alpha('#EF4444', 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <UndoIcon sx={{ color: '#EF4444', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
            Refund Sale
          </Typography>
          {sale.receiptNumber && (
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {sale.receiptNumber}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
          </Alert>
        )}

        {/* Sale Summary */}
        <Box
          sx={{
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: '10px',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography color="text.secondary">Sale Total</Typography>
            <Typography fontWeight={600}>{formatPrice(sale.totalAmount)}</Typography>
          </Box>
          {sale.refundedAmount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography color="text.secondary">Already Refunded</Typography>
              <Typography color="warning.main" fontWeight={600}>
                -{formatPrice(sale.refundedAmount)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Refund Type Selection */}
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          Refund Type
        </Typography>
        <RadioGroup
          value={refundType}
          onChange={(e) => setRefundType(e.target.value as 'full' | 'partial')}
          sx={{ mb: 2 }}
        >
          <FormControlLabel
            value="full"
            control={<Radio />}
            label={`Full Refund (${formatPrice(sale.totalAmount - sale.refundedAmount)})`}
          />
          <FormControlLabel
            value="partial"
            control={<Radio />}
            label="Partial Refund"
          />
        </RadioGroup>

        {/* Item Selection for Partial Refund */}
        {refundType === 'partial' && (
          <Box sx={{ mb: 3 }}>
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              Select Items to Refund
            </Typography>
            {errors.items && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {errors.items}
              </Alert>
            )}
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '10px' }}>
              {sale.items.map((item, index) => {
                const selected = selectedItems.get(item.id);
                const isSelected = !!selected;

                return (
                  <Box key={item.id}>
                    {index > 0 && <Divider />}
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleItemToggle(item)}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={500}>{item.productName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatPrice(item.unitPrice)} × {item.quantity} = {formatPrice(item.subtotal)}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <TextField
                          type="number"
                          size="small"
                          label={`Quantity for ${item.productName}`}
                          value={selected?.quantity || item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(item.id, parseInt(e.target.value) || 1)
                          }
                          inputProps={{
                            min: 1,
                            max: item.quantity,
                            'aria-label': `Quantity for ${item.productName}`,
                          }}
                          sx={{ width: 100 }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Reason Selection */}
        <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.reason}>
          <InputLabel id="refund-reason-label">Reason *</InputLabel>
          <Select
            labelId="refund-reason-label"
            id="refund-reason"
            value={reason}
            label="Reason *"
            onChange={(e) => {
              setReason(e.target.value);
              setErrors((prev) => ({ ...prev, reason: undefined }));
            }}
            inputProps={{ 'aria-label': 'Reason' }}
          >
            {REFUND_REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
          {errors.reason && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
              {errors.reason}
            </Typography>
          )}
        </FormControl>

        {/* Notes */}
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this refund"
          inputProps={{ 'aria-label': 'Notes' }}
        />

        {/* Refund Summary */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: alpha('#EF4444', 0.05),
            borderRadius: '10px',
            border: `1px solid ${alpha('#EF4444', 0.2)}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography fontWeight={600}>Refund Amount</Typography>
            <Typography variant="h5" fontWeight={700} color="error.main">
              {formatPrice(refundAmount)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <UndoIcon />}
        >
          {isLoading ? 'Processing...' : 'Process Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundModal;
